import { datadogLogs } from "@datadog/browser-logs";
import { formatISO } from "date-fns";
import { some } from "lodash";
import { Middleware, UnknownAction } from "redux";

import { LocalStorageRingBuffer } from "./local_storage_ring_buffer";

import { DATADOG_ENABLED } from "config";
import { ActionLogType, LogType, LoggerType } from "shared/types";
import { stringifyArgs } from "shared/utils/logger";

class Logger implements LoggerType {
  private actionLogs: LocalStorageRingBuffer<ActionLogType>;

  private consoleLogs: LocalStorageRingBuffer<LogType>;

  private actionLogBufferSize = 1000;

  private consoleLogBufferSize = 1000;

  constructor(classId: string) {
    this.actionLogs = new LocalStorageRingBuffer(
      "actions",
      this.actionLogBufferSize,
      true,
      classId,
    );
    this.consoleLogs = new LocalStorageRingBuffer(
      "console",
      this.consoleLogBufferSize,
      true,
      classId,
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  actionLogger: Middleware = (store) => (dispatch) => (action) => {
    const timestamp = Date.now();
    this.actionLogs.push({ timestamp, action: action as UnknownAction });
    return dispatch(action);
  };

  static prefixForLog(log: LogType) {
    const prefix = log.type !== "log" ? `${log.type.toUpperCase()}: ` : "";
    return `[${formatISO(log.timestamp)}] ${prefix}`;
  }

  static prettyPrintActionLog(log: ActionLogType) {
    const prefix = `[${formatISO(log.timestamp)}] `;
    const { type } = log.action;
    if (type === "SYNC") {
      return `${prefix}${JSON.stringify({ type })}`;
    }
    return `${prefix}${JSON.stringify(log.action)}`;
  }

  static prettyPrintLog(log: LogType) {
    return `${Logger.prefixForLog(log)}${log.msg}`;
  }

  bufferedLog(args: unknown[], type: "log" | "warn" | "error") {
    const timestamp = Date.now();
    const msg = stringifyArgs(args);
    const error = args.find((arg) => arg instanceof Error) as Error | undefined;
    const log: LogType = { msg, timestamp, type, error };
    this.consoleLogs.push(log);
    const enableForumLogs = sessionStorage.getItem(`enableForumLogs`) || "false";
    if (type === "log" && !JSON.parse(enableForumLogs)) {
      if (DATADOG_ENABLED) {
        // Skip specific logs
        if (some(["[PubsubWebSocket]"], (str) => log.msg.includes(str))) return;

        // Since we are not logging to the console, send log to Datadog directly
        datadogLogs.logger.info(Logger.prettyPrintLog(log));
      }
      return;
    }
    // eslint-disable-next-line no-console
    console[log.type](Logger.prettyPrintLog(log), {}, error);
  }

  log(...args: unknown[]) {
    this.bufferedLog(args, "log");
  }

  warn(...args: unknown[]) {
    this.bufferedLog(args, "warn");
  }

  error(...args: unknown[]) {
    this.bufferedLog(args, "error");
  }

  actionLogArray() {
    return this.actionLogs.toArray();
  }

  consoleLogArray() {
    return this.consoleLogs.toArray();
  }
}

export { Logger };
